/**
 * Ratebook row field names for payment breakdown (modal + cards).
 *
 * Backend exposes these as scalars on each ratebook row / vehicle item.
 * If the API ever renames them, change only the string values below and
 * make sure the fields are still selected in:
 *   - src/graphql/queries/QueryRatebook.js
 *   - src/graphql/queries/QueryVehicleByCatalogId.js (items + variants)
 */

export const RATEBOOK_PAYMENT_BREAKDOWN_FIELDS = {
  vehicleFinance: {
    week: 'vehicleFinanceWeekly',
    month: 'vehicleFinance'
  },
  bundledRunningCosts: {
    week: 'vehicleRunningCostWeekly',
    month: 'vehicleRunningCost'
  }
};

/**
 * @param {object|null|undefined} row — ratebook item / variant containing the four backend fields
 * @param {boolean} isWeekly
 * @returns {{ finance: number|null, bundled: number|null }}
 */
export function getPaymentBreakdownNumericValues(row, isWeekly) {
  if (!row || typeof row !== 'object') return { finance: null, bundled: null };
  const period = isWeekly ? 'week' : 'month';
  const financeKey = RATEBOOK_PAYMENT_BREAKDOWN_FIELDS.vehicleFinance[period];
  const bundledKey = RATEBOOK_PAYMENT_BREAKDOWN_FIELDS.bundledRunningCosts[period];
  const finance = row[financeKey];
  const bundled = row[bundledKey];
  const toNum = (v) => {
    if (v == null || Number.isNaN(Number(v))) return null;
    return Number(v);
  };
  return { finance: toNum(finance), bundled: toNum(bundled) };
}

/** Shown in the modal when a formatted amount prop is missing or blank */
export const PAYMENT_BREAKDOWN_AMOUNT_PLACEHOLDER = '\u2014';

/**
 * @param {string|null|undefined} amountString — already formatted (e.g. from formatPaymentBreakdownAmount)
 * @returns {string}
 */
export function resolvePaymentBreakdownAmountDisplay(amountString) {
  if (amountString == null) return PAYMENT_BREAKDOWN_AMOUNT_PLACEHOLDER;
  const s = String(amountString).trim();
  return s === '' ? PAYMENT_BREAKDOWN_AMOUNT_PLACEHOLDER : s;
}

/** Same formatting as vehicle product header / cards for currency display */
export function formatPaymentBreakdownAmount(value) {
  if (value == null || Number.isNaN(Number(value))) return '';
  return `$${Number(value).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * @param {object|null|undefined} row
 * @param {boolean} isWeekly
 * @returns {{ finance: string, bundled: string }}
 */
export function getPaymentBreakdownFormattedParts(row, isWeekly) {
  const { finance, bundled } = getPaymentBreakdownNumericValues(row, isWeekly);
  return {
    finance: formatPaymentBreakdownAmount(finance),
    bundled: formatPaymentBreakdownAmount(bundled)
  };
}
